import {
    createContext,
    useContext,
    useMemo,
    useState,
    useCallback,
} from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

export interface FontEntry {
    family: string;
    weight: number;
}

const fe = (family: string, weight = 400): FontEntry => ({ family, weight });

export const FONT_PRESETS: { label: string; fonts: FontEntry[] }[] = [
    {
        label: "Serif Mix",
        fonts: ["Times New Roman", "Georgia", "Garamond", "Palatino Linotype"].map(f => fe(f)),
    },
    {
        label: "Sans-Serif Mix",
        fonts: ["Arial", "Helvetica", "Verdana", "Trebuchet MS", "Tahoma"].map(f => fe(f)),
    },
    {
        label: "Monospace Mix",
        fonts: ["Courier New", "Consolas", "Lucida Console"].map(f => fe(f)),
    },
    {
        label: "Handwriting",
        fonts: ["Comic Sans MS", "Brush Script MT", "Segoe Script", "Ink Free"].map(f => fe(f)),
    },
    {
        label: "Classic Mix",
        fonts: ["Arial", "Times New Roman", "Georgia", "Verdana", "Courier New"].map(f => fe(f)),
    },
    {
        label: "Wild Mix",
        fonts: ["Impact", "Comic Sans MS", "Georgia", "Courier New", "Arial Black"].map(f => fe(f)),
    },
    { label: "Custom", fonts: [] },
];

export interface CharLayout {
    char: string;
    x: number;
    y: number;
    font: string;
    weight: number;
    fontSize: number;
}

interface FontMixerState {
    text: string;
    fontSize: number;
    selectedFonts: FontEntry[];
    seed: number;
    letterSpacing: number;
    lineHeight: number;
    normalizeHeight: boolean;
    exportAsOutlines: boolean;
    activePreset: string;
    charLayout: CharLayout[];
    svgWidth: number;
    svgHeight: number;
    viewBoxAttr: string;
    canExport: boolean;

    setText: (text: string) => void;
    setFontSize: (size: number) => void;
    setSelectedFonts: React.Dispatch<React.SetStateAction<FontEntry[]>>;
    setSeed: (seed: number) => void;
    setLetterSpacing: (spacing: number) => void;
    setLineHeight: (height: number) => void;
    setNormalizeHeight: (normalize: boolean) => void;
    setExportAsOutlines: (enabled: boolean) => void;
    setActivePreset: (preset: string) => void;
    handleDownload: () => Promise<void>;
    buildExportSvgString: () => Promise<string>;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const FontMixerContext = createContext<FontMixerState | null>(null);

export function useFontMixer() {
    const context = useContext(FontMixerContext);
    if (!context) {
        throw new Error("useFontMixer must be used within FontMixerProvider");
    }
    return context;
}

function mulberry32(seed: number) {
    let s = seed | 0;
    return () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = Math.imul(s ^ (s >>> 15), 1 | s);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const measureCanvas = document.createElement("canvas");
const measureCtx = measureCanvas.getContext("2d")!;

function measureChar(char: string, font: string, weight: number, fontSize: number): number {
    measureCtx.font = `${weight} ${fontSize}px "${font}"`;
    return measureCtx.measureText(char).width;
}

function measureCapHeight(font: string, weight: number, fontSize: number): number {
    measureCtx.font = `${weight} ${fontSize}px "${font}"`;
    const metrics = measureCtx.measureText("H");
    return metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
}

function fontEntryKey(e: FontEntry): string {
    return `${e.family}\0${e.weight}`;
}

interface FontMixerProviderProps {
    children: React.ReactNode;
}

const DEFAULT_FONT_MIXER_PRESET: Record<string, unknown> = {
    text: "The quick brown fox\njumps over the lazy dog",
    fontSize: 32,
    selectedFonts: FONT_PRESETS[0].fonts.map((f) => ({ ...f })),
    seed: 42,
    letterSpacing: 2,
    lineHeight: 1.4,
    normalizeHeight: false,
    exportAsOutlines: false,
    activePreset: "Serif Mix",
};

export function FontMixerProvider({ children }: FontMixerProviderProps) {
    const [text, setText] = useState("The quick brown fox\njumps over the lazy dog");
    const [fontSize, setFontSize] = useState(32);
    const [selectedFonts, setSelectedFonts] = useState<FontEntry[]>(
        FONT_PRESETS[0].fonts
    );
    const [seed, setSeed] = useState(42);
    const [letterSpacing, setLetterSpacing] = useState(2);
    const [lineHeight, setLineHeight] = useState(1.4);
    const [normalizeHeight, setNormalizeHeight] = useState(false);
    const [exportAsOutlines, setExportAsOutlines] = useState(false);
    const [activePreset, setActivePreset] = useState("Serif Mix");

    const { charLayout, svgWidth, svgHeight } = useMemo(() => {
        if (!text || selectedFonts.length === 0) {
            return { charLayout: [], svgWidth: 0, svgHeight: 0 };
        }

        const adjustedSizeMap = new Map<string, number>();
        if (normalizeHeight) {
            let maxCapHeight = 0;
            for (const entry of selectedFonts) {
                const key = fontEntryKey(entry);
                const ch = measureCapHeight(entry.family, entry.weight, fontSize);
                adjustedSizeMap.set(key, ch);
                if (ch > maxCapHeight) maxCapHeight = ch;
            }
            if (maxCapHeight > 0) {
                for (const entry of selectedFonts) {
                    const key = fontEntryKey(entry);
                    const ch = adjustedSizeMap.get(key)!;
                    adjustedSizeMap.set(key, ch > 0 ? fontSize * (maxCapHeight / ch) : fontSize);
                }
            }
        }

        const rng = mulberry32(seed);
        const lines = text.split("\n");
        const layout: CharLayout[] = [];
        const lineSpacing = fontSize * lineHeight;
        const padding = fontSize;
        let maxLineWidth = 0;

        for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
            const line = lines[lineIdx];
            let x = padding;
            const y = padding + lineIdx * lineSpacing + fontSize;

            for (let charIdx = 0; charIdx < line.length; charIdx++) {
                const char = line[charIdx];
                const entry =
                    char.trim() === ""
                        ? selectedFonts[0]
                        : selectedFonts[Math.floor(rng() * selectedFonts.length)];

                const effectiveFontSize = normalizeHeight
                    ? (adjustedSizeMap.get(fontEntryKey(entry)) ?? fontSize)
                    : fontSize;
                const charWidth = measureChar(char, entry.family, entry.weight, effectiveFontSize);

                layout.push({ char, x, y, font: entry.family, weight: entry.weight, fontSize: effectiveFontSize });
                x += charWidth + letterSpacing;
            }

            maxLineWidth = Math.max(maxLineWidth, x);
        }

        const totalHeight = padding + lines.length * lineSpacing + padding;
        const totalWidth = maxLineWidth + padding;

        return {
            charLayout: layout,
            svgWidth: Math.ceil(totalWidth),
            svgHeight: Math.ceil(totalHeight),
        };
    }, [text, fontSize, selectedFonts, seed, letterSpacing, lineHeight, normalizeHeight]);

    const viewBoxAttr = `0 0 ${svgWidth} ${svgHeight}`;
    const canExport = charLayout.length > 0;

    const buildExportSvgString = useCallback(async () => {
        if (!charLayout.length) return "";

        const escapeXml = (s: string) =>
            s
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;");

        if (!exportAsOutlines) {
            const textElements = charLayout
                .map(
                    (c) =>
                        `    <text x="${c.x}" y="${c.y}" font-family="${escapeXml(c.font)}" font-weight="${c.weight}" font-size="${c.fontSize}" fill="black">${escapeXml(c.char)}</text>`
                )
                .join("\n");

            return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="font-mixer">
${textElements}
  </g>
</svg>`;
        }

        const fontAccess = (window as Window & { queryLocalFonts?: () => Promise<FontData[]> }).queryLocalFonts;
        if (!fontAccess) {
            console.warn("Font Mixer: Local Font Access API unavailable, exporting text nodes.");
            const textElements = charLayout
                .map(
                    (c) =>
                        `    <text x="${c.x}" y="${c.y}" font-family="${escapeXml(c.font)}" font-weight="${c.weight}" font-size="${c.fontSize}" fill="black">${escapeXml(c.char)}</text>`
                )
                .join("\n");

            return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="font-mixer">
${textElements}
  </g>
</svg>`;
        }

        const opentypeModule = await import("opentype.js");
        const opentype = (opentypeModule as any).default ?? opentypeModule;
        const localFonts = await fontAccess();
        const familyMap = new Map<string, FontData[]>();
        for (const f of localFonts) {
            const key = f.family.toLowerCase();
            const list = familyMap.get(key);
            if (list) list.push(f);
            else familyMap.set(key, [f]);
        }

        const resolveFont = async (family: string, weight: number) => {
            const candidates = familyMap.get(family.toLowerCase());
            if (!candidates || candidates.length === 0) return null;

            const wantBold = weight >= 600;
            const styleRank = (style: string) => {
                const s = style.toLowerCase();
                const isBold = s.includes("bold") || s.includes("black") || s.includes("heavy");
                if (wantBold) return isBold ? 2 : 1;
                return isBold ? 1 : 2;
            };
            const selected = [...candidates].sort((a, b) => styleRank(b.style) - styleRank(a.style))[0];
            const blob = await selected.blob();
            const buf = await blob.arrayBuffer();
            return opentype.parse(buf);
        };

        const fontCache = new Map<string, any | null>();
        const outlineElements = await Promise.all(
            charLayout.map(async (c) => {
                if (c.char.trim() === "") return "";
                const key = `${c.font}\0${c.weight}`;
                let font = fontCache.get(key);
                if (font === undefined) {
                    try {
                        font = await resolveFont(c.font, c.weight);
                    } catch {
                        font = null;
                    }
                    fontCache.set(key, font ?? null);
                }

                if (!font) {
                    return `    <text x="${c.x}" y="${c.y}" font-family="${escapeXml(c.font)}" font-weight="${c.weight}" font-size="${c.fontSize}" fill="black">${escapeXml(c.char)}</text>`;
                }

                const pathData = font.getPath(c.char, c.x, c.y, c.fontSize).toPathData(3);
                return `    <path d="${escapeXml(pathData)}" fill="black" />`;
            })
        );

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="font-mixer">
${outlineElements.join("\n")}
  </g>
</svg>`;
    }, [charLayout, exportAsOutlines, viewBoxAttr]);

    const handleDownload = useCallback(async () => {
        if (!canExport) return;
        const svgString = await buildExportSvgString();
        if (!svgString) return;

        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `font-mixer-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [canExport, buildExportSvgString]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            text, fontSize, selectedFonts, seed, letterSpacing, lineHeight,
            normalizeHeight, exportAsOutlines, activePreset,
        }),
        [
            text, fontSize, selectedFonts, seed, letterSpacing, lineHeight,
            normalizeHeight, exportAsOutlines, activePreset,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.text != null) setText(values.text as string);
        if (values.fontSize != null) setFontSize(values.fontSize as number);
        if (values.selectedFonts != null) setSelectedFonts(values.selectedFonts as FontEntry[]);
        if (values.seed != null) setSeed(values.seed as number);
        if (values.letterSpacing != null) setLetterSpacing(values.letterSpacing as number);
        if (values.lineHeight != null) setLineHeight(values.lineHeight as number);
        if (values.normalizeHeight != null) setNormalizeHeight(values.normalizeHeight as boolean);
        if (values.exportAsOutlines != null) setExportAsOutlines(values.exportAsOutlines as boolean);
        if (values.activePreset != null) setActivePreset(values.activePreset as string);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "font-mixer",
        defaultPreset: DEFAULT_FONT_MIXER_PRESET,
        getPresetValues,
        applyPreset,
    });

    const value: FontMixerState = {
        text,
        fontSize,
        selectedFonts,
        seed,
        letterSpacing,
        lineHeight,
        normalizeHeight,
        exportAsOutlines,
        activePreset,
        charLayout,
        svgWidth,
        svgHeight,
        viewBoxAttr,
        canExport,
        setText,
        setFontSize,
        setSelectedFonts,
        setSeed,
        setLetterSpacing,
        setLineHeight,
        setNormalizeHeight,
        setExportAsOutlines,
        setActivePreset,
        handleDownload,
        buildExportSvgString,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <FontMixerContext.Provider value={value}>
            {children}
        </FontMixerContext.Provider>
    );
}
